/* eslint-disable import/no-cycle */
/* eslint-disable consistent-return */
import { onError } from '@apollo/client/link/error';
import {
  ApolloClient,
  ApolloLink,
  DefaultOptions,
  from,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  Observable
} from '@apollo/client';
import constants from '@/core/constants';
import { authManager } from '../../../core/managers';
import { KeycloakManager } from '../../../core/managers/keycloakManager';

const clientOptions = {
  isReservation: false
};

const cache = new InMemoryCache({
  addTypename: false
});

const defaultOptions: DefaultOptions = {
  query: {
    fetchPolicy: 'network-only',
    errorPolicy: 'all'
  },
  mutate: {
    errorPolicy: 'all'
  }
};

export class ClientSingleton {
  apiClient: ApolloClient<NormalizedCacheObject>;

  options: any;

  private static instance: ClientSingleton;

  errorLink = onError(({ operation, forward, networkError }: any) => {
    if (
      networkError &&
      ((networkError.result &&
        networkError.result?.message ===
          'User is not authorized to access this resource with an explicit deny') ||
        networkError.message === 'Failed to fetch')
    ) {
      return new Observable((observer) => {
        const { refreshToken, userId, role } =
          KeycloakManager.getInstance().getToken();
        if (!refreshToken) {
          return forward(operation);
        }
        KeycloakManager.getInstance()
          .refreshToken(1000 * 60 * 60 * 12)
          .then((response) => {
            const idSimulated = authManager.getCredentials()?.userIdSimulated;
            if (idSimulated)
              operation.setContext({
                headers: {
                  Authorization: `Bearer ${response.token}`,
                  isReservation: this.options.isReservation,
                  applicationId: 'APP00002',
                  // enviar el userID --> 'user-id': authManager.getCredentials()?.userId,
                  'user-id': userId,
                  'user-role': role,
                  'user-id-simulated':
                    authManager.getCredentials()?.userIdSimulated
                }
              });
            operation.setContext({
              headers: {
                Authorization: `Bearer ${response.token}`,
                isReservation: this.options.isReservation,
                applicationId: 'APP00002',
                // enviar el userID --> 'user-id': authManager.getCredentials()?.userId,
                'user-id': userId,
                'user-role': role
              }
            });
            forward(operation).subscribe({
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer)
            });
          })
          .catch((error) => {
            observer.error(error);
          });
      });
    }
  });

  authLink = new ApolloLink((operation, forward) => {
    const { token, userId, role } = KeycloakManager.getInstance().getToken();
    if (token === undefined) return null;
    operation.setContext(({ headers = {} }) => {
      const idSimulated = authManager.getCredentials()?.userIdSimulated;
      if (idSimulated)
        return {
          timeout: 1000 * 60 * 3,
          headers: {
            ...headers,
            authorization: `Bearer ${token}` || null,
            isReservation: this.options.isReservation,
            applicationId: 'APP00002',
            'user-id': userId,
            'user-role': role,
            'user-id-simulated': authManager.getCredentials()?.userIdSimulated
          }
        };
      return {
        timeout: 1000 * 60 * 3,
        headers: {
          ...headers,
          authorization: `Bearer ${token}` || null,
          isReservation: this.options.isReservation,
          applicationId: 'APP00002',
          'user-id': userId,
          'user-role': role
        }
      };
    });
    return forward(operation);
  });

  httpLink = new HttpLink({
    uri: constants.GRAPHQL_URL
  });

  private constructor(options: any) {
    this.options = { ...options };

    this.apiClient = new ApolloClient({
      link: from([this.authLink, this.errorLink, this.httpLink]),
      cache,
      queryDeduplication: false,
      defaultOptions
    });
  }

  public static getInstance(): ClientSingleton {
    if (!ClientSingleton.instance) {
      ClientSingleton.instance = new ClientSingleton(clientOptions);
    }
    return ClientSingleton.instance;
  }

  public static async clearInstance() {
    ClientSingleton.instance = new ClientSingleton(clientOptions);
    await ClientSingleton.instance.apiClient.clearStore();
  }
}

export function resetClient() {
  ClientSingleton.clearInstance();
}

export default function apiClient() {
  return ClientSingleton.getInstance().apiClient;
}
